import { useCallback, useState } from 'react';
import { useSpeedDialer } from '../../context/speed-dialer.context';
import { usePhoneContext } from 'src/contexts/phone-context';
import { useGlobalCallProviderContext } from 'src/contexts/global-call-provider-context';
import STATES from 'states/index';

/**
 * Shared hook for speed dial control logic used by both inline and popover components
 */
export const useSpeedDialControls = () => {
  const {
    status,
    queue,
    stopSession,
    pauseSession,
    resumeSession,
    skipCall,
    currentConversationId,
    currentPhoneNumber,
    currentPhoneIndex,
    currentAttempts,
    maxAttempts,
  } = useSpeedDialer();

  const { phoneState, isMuted, setIsMuted } = usePhoneContext();
  const { provider: callProvider, loading: providerLoading } =
    useGlobalCallProviderContext();
  const [showStopConfirmation, setShowStopConfirmation] = useState(false);

  const isPaused = status === 'paused';
  const isDialerActive = status !== 'idle';
  const isCallActive =
    phoneState === STATES.PHONE.ESTABLISHED ||
    phoneState === STATES.PHONE.OUTGOING ||
    phoneState === STATES.PHONE.INCOMING;

  let computedRemainingCalls: number;
  let currentQueueItem = null;
  if (currentConversationId) {
    const currentIndex = queue.findIndex(
      (item) => item.conversationId === currentConversationId
    );
    currentQueueItem = currentIndex !== -1 ? queue[currentIndex] : null;
    if (currentQueueItem) {
      computedRemainingCalls =
        currentQueueItem.phoneNumbers.length -
        currentPhoneIndex +
        queue
          .slice(currentIndex + 1)
          .reduce((sum, item) => sum + item.phoneNumbers.length, 0);
    } else {
      computedRemainingCalls = queue.reduce(
        (total: number, item) => total + item.phoneNumbers.length,
        0
      );
    }
  } else {
    computedRemainingCalls = queue.reduce(
      (total: number, item) => total + item.phoneNumbers.length,
      0
    );
  }

  const handleEndCall = useCallback(() => {
    if (providerLoading || !callProvider) return;
    callProvider.callActions.endCall();
  }, [callProvider, providerLoading]);

  const handleMute = useCallback(() => {
    if (providerLoading || !callProvider) return;
    const newMutedState = !isMuted;
    setIsMuted(newMutedState);
    callProvider.callActions.muteCall(newMutedState);
  }, [callProvider, providerLoading, isMuted, setIsMuted]);

  const handlePlayPause = useCallback(() => {
    if (isPaused) {
      resumeSession();
    } else {
      pauseSession();
    }
  }, [isPaused, resumeSession, pauseSession]);

  const handleConfirmStop = useCallback(() => {
    stopSession();
    setShowStopConfirmation(false);
  }, [stopSession]);

  const handleSkip = useCallback(() => {
    skipCall();
  }, [skipCall]);

  return {
    // State
    isPaused,
    isDialerActive,
    isCallActive,
    isMuted,
    currentQueueItem,
    currentPhoneNumber,
    currentAttempts,
    computedRemainingCalls,
    showStopConfirmation,
    maxAttempts,

    // Actions
    handleMute,
    handleEndCall,
    handlePlayPause,
    handleSkip,
    handleConfirmStop,
    setShowStopConfirmation,
  };
};
